/* eslint-disable @typescript-eslint/no-explicit-any */

import { WindowTypeEnum } from '@ben/api/gen/codegen.schemas';
import { parseDateValue } from '@ben/common/date';
import Checkbox from '@ben/orion/checkbox';
import type { DateInputRangeValue } from '@ben/orion/date-input';
import DateInput from '@ben/orion/date-input';
import Button from '@ben/orion/deprecated-button';
import InlineTextButton from '@ben/orion/inline-text-button';
import Input from '@ben/orion/input';
import RadioButton from '@ben/orion/radio-button';
import Select from '@ben/orion/select';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { ITheme } from '@ben/orion/utils/theme';
import {
  type IBenefitWindow,
  type IModularBenefitDetail,
  type IModularBenefitMessages,
} from '@ben/shared/types/columbia';
import { StartsOnEnum } from '@ben/shared/types/modular-benefit';
import { TimeInput } from '@mantine/dates';
import { add, format } from 'date-fns';
import { isEmpty } from 'lodash';
import { memo, useCallback, useEffect, useState } from 'react';
import type { Control, FieldPath, RegisterOptions } from 'react-hook-form';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import type { ITimezone } from 'react-timezone-select';
import { allTimezones, useTimezoneSelect } from 'react-timezone-select';
import styled, { css, useTheme } from 'styled-components';
import {
  TIME_UNIT_OPTIONS,
  fieldCannotBeBlankErrorMessage,
} from '../../shared/constants';
import { useSetDirtyValue } from '../hooks/use-set-dirty-value';
import { AirtableImportFeedback } from '../shared/airtable-import-feedback';
import EnrolmentStartsOnDate from './enrolment-starts-on-date';
import EnrolmentWindowType from './enrolment-window-type';
import LifeEvents from './live-events';

const emptyString = undefined;
const minDate = new Date();
const maxDate = add(new Date(), { years: 20 });

export const emptyBenefitWindow: IBenefitWindow = {
  id: undefined,
  endsAfterDays: emptyString,
  startsOn: StartsOnEnum.EMPLOYMENT_START,
  windowType: WindowTypeEnum.CONFIG_WINDOW,
  startsOnSpecificDate: emptyString,
  endsOnSpecificDate: emptyString,
  startsOnSpecificTime: emptyString,
  endsOnSpecificTime: emptyString,
  fixedEnrolmentStartDate: emptyString,
  timezone: 'Etc/GMT',
  endsAfter: emptyString,
  timeUnit: emptyString,
  ageThreshold: emptyString,
  startsAfterDelay: emptyString,
  startsBeforeDays: emptyString,
};

export enum WindowTriggerType {
  RULE_BASED = 'RULE_BASED',
  DATE_RANGE = 'DATE_RANGE',
}

interface NumberInputControllerProps {
  name: FieldPath<IModularBenefitDetail>;
  control: Control<IModularBenefitDetail>;
  label: string;
  hintText: string;
  inputKey: string;
  rules?: RegisterOptions<IModularBenefitDetail>;
  disabled?: boolean;
  min?: number;
  'data-testid'?: string;
  /** Extra gate applied alongside `!!fieldState.error`. Defaults to true. */
  hasErrorCondition?: boolean;
}

const NumberInputController = ({
  name,
  control,
  label,
  hintText,
  inputKey,
  rules,
  disabled,
  min: minValue,
  'data-testid': testId,
  hasErrorCondition = true,
}: NumberInputControllerProps) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field, fieldState }) => {
      const { value, ...rest } = field;
      return (
        <Input
          key={inputKey}
          {...rest}
          name={label}
          type="number"
          min={minValue}
          value={value ?? ''}
          hasError={hasErrorCondition && !!fieldState.error}
          errorMessage={fieldState.error?.message}
          width="240px"
          hintText={hintText}
          disabled={disabled}
          data-testid={testId}
        />
      );
    }}
  />
);

interface EnrolmentWindowsProps {
  errors?: any;
  successes: IModularBenefitMessages;
}

const EnrolmentWindows = ({ errors, successes }: EnrolmentWindowsProps) => {
  const theme = useTheme();

  const { options, parseTimezone } = useTimezoneSelect({
    labelStyle: 'original',
    timezones: allTimezones,
  });

  const { control, getFieldState, setValue } =
    useFormContext<IModularBenefitDetail>();
  const benefitWindows = useWatch({ name: 'benefitWindows', exact: true });
  const setDirtyValue = useSetDirtyValue<IModularBenefitDetail>();

  const clearStaleWindowFields = useCallback(
    (index: number, window: IBenefitWindow, startsOn: string | undefined) => {
      if (
        startsOn !== StartsOnEnum.AGE_THRESHOLD &&
        window.ageThreshold != null
      ) {
        setDirtyValue(`benefitWindows.${index}.ageThreshold`, null);
      }
      if (startsOn === StartsOnEnum.BEFORE_ENROLLMENT_END) {
        if (
          window.endsAfter != null ||
          window.timeUnit != null ||
          window.startsAfterDelay != null
        ) {
          setDirtyValue(`benefitWindows.${index}.endsAfter`, null);
          setDirtyValue(`benefitWindows.${index}.timeUnit`, null);
          setDirtyValue(`benefitWindows.${index}.startsAfterDelay`, null);
        }
      } else if (window.startsBeforeDays != null) {
        setDirtyValue(`benefitWindows.${index}.startsBeforeDays`, null);
      }
    },
    [setDirtyValue]
  );

  const { fields, append, remove } = useFieldArray({
    name: 'benefitWindows',
    control,
  });

  const startsOnValues = useWatch({
    name: fields.map((_, index) => `benefitWindows.${index}.startsOn` as const),
  });

  const windowTypeValues = useWatch({
    name: fields.map(
      (_, index) => `benefitWindows.${index}.windowType` as const
    ),
  });

  const fixedEnrolmentStartDateValues = useWatch({
    name: fields.map(
      (_, index) => `benefitWindows.${index}.fixedEnrolmentStartDate` as const
    ),
  });

  const startsOnSpecificDateValues = useWatch({
    name: fields.map(
      (_, index) => `benefitWindows.${index}.startsOnSpecificDate` as const
    ),
  });

  const endsOnSpecificDateValues = useWatch({
    name: fields.map(
      (_, index) => `benefitWindows.${index}.endsOnSpecificDate` as const
    ),
  });

  const [windowTriggerTypes, setWindowTriggerTypes] = useState<
    WindowTriggerType[]
  >([]);

  useEffect(() => {
    if (benefitWindows) {
      const initialTriggerTypes = benefitWindows.map(
        (window: {
          startsOnSpecificDate?: string;
          endsOnSpecificDate?: string;
        }) =>
          window.startsOnSpecificDate && window.endsOnSpecificDate
            ? WindowTriggerType.DATE_RANGE
            : WindowTriggerType.RULE_BASED
      );
      setWindowTriggerTypes(initialTriggerTypes);
    }
  }, [benefitWindows]);

  useEffect(() => {
    if (benefitWindows && !isEmpty(startsOnValues)) {
      for (const [index, window] of benefitWindows.entries()) {
        clearStaleWindowFields(index, window, startsOnValues[index]);
      }
    }
  }, [benefitWindows, startsOnValues, clearStaleWindowFields]);

  useEffect(() => {
    if (!benefitWindows || isEmpty(windowTypeValues)) return;
    for (const [index, window] of benefitWindows.entries()) {
      if (
        windowTypeValues[index] !== WindowTypeEnum.OPT_IN_WINDOW &&
        window.fixedEnrolmentStartDate != null
      ) {
        setDirtyValue(`benefitWindows.${index}.fixedEnrolmentStartDate`, null);
      }
    }
  }, [benefitWindows, windowTypeValues, setDirtyValue]);

  const addNewWindow = () => {
    if (fields.length === 0) {
      setValue('benefitWindows', []);
    }
    append(emptyBenefitWindow);
    setWindowTriggerTypes((previous) => [
      ...previous,
      WindowTriggerType.RULE_BASED,
    ]);
  };

  const getWindowType = (windowIndex: number) => {
    return benefitWindows?.[windowIndex]?.windowType;
  };

  return (
    <Wrapper>
      <HeaderWrapper>
        <Typography variant={TypographyVariant.h2}>Benefit windows</Typography>
        <Button label="Add benefit window" onClick={addNewWindow} />
      </HeaderWrapper>
      <Controller
        name="closeConfigWindowAfterOptIn"
        control={control}
        render={({ field }) => {
          return (
            <Checkbox
              label="Close config window after opt-in"
              {...field}
              value={field.value || false}
            />
          );
        }}
      />
      <Controller
        name="cancelEnrolmentOnOptOutDuringOptInWindow"
        control={control}
        render={({ field, fieldState: { isDirty } }) => {
          return (
            <Checkbox
              label="Cancel enrolment on opt-out during opt-in window"
              {...field}
              value={field.value || false}
              successMessage={
                isDirty
                  ? undefined
                  : successes.cancelEnrolmentOnOptOutDuringOptInWindow
              }
            />
          );
        }}
      />
      <Controller
        name="closeOptInWindowAfterOptOut"
        control={control}
        render={({ field, fieldState: { isDirty } }) => {
          return (
            <Checkbox
              {...field}
              value={field.value || false}
              label={'Close opt in window after opt out'}
              successMessage={
                isDirty ? undefined : successes.closeOptInWindowAfterOptOut
              }
            />
          );
        }}
      />
      <Controller
        name="closeWindowsOnFulfillment"
        control={control}
        render={({ field }) => {
          return (
            <Checkbox
              label="Close windows after fulfilment"
              {...field}
              value={field.value || false}
            />
          );
        }}
      />
      <Controller
        name="hideClosedConfigWindowBanner"
        control={control}
        render={({ field }) => {
          return (
            <Checkbox
              label="Hide closed config window banner"
              {...field}
              value={field.value || false}
            />
          );
        }}
      />
      {fields?.length === 0 && (
        <AddAndNoSubPriceWrapper>
          <Typography variant={TypographyVariant.bodyLg1}>
            No windows added yet
          </Typography>
        </AddAndNoSubPriceWrapper>
      )}

      {fields.map((benefitWindow, index) => {
        const { isDirty } = getFieldState(`benefitWindows.${index}`);
        const windowTriggerType = windowTriggerTypes[index];
        const startsOnSpecificDate = startsOnSpecificDateValues[index];
        const endsOnSpecificDate = endsOnSpecificDateValues[index];
        const isBeforeEnrollmentEnd =
          startsOnValues?.[index] === StartsOnEnum.BEFORE_ENROLLMENT_END;
        const dateRangeValue =
          !!startsOnSpecificDate && !!endsOnSpecificDate
            ? ([
                parseDateValue(startsOnSpecificDate),
                parseDateValue(endsOnSpecificDate),
              ] as DateInputRangeValue)
            : ([undefined, undefined] as unknown as undefined);

        return (
          <ColumnWrapper key={benefitWindow.id}>
            <div>
              <HeaderWrapper>
                <Typography variant={TypographyVariant.h3}>
                  Window #{index + 1}
                </Typography>
                <StyledInlineTextButton
                  label="Remove"
                  aria-label="Remove"
                  onClick={() => {
                    remove(index);
                    setWindowTriggerTypes((previous) =>
                      previous.filter((_, index_) => index_ !== index)
                    );
                  }}
                />
              </HeaderWrapper>
              <AirtableImportFeedback
                visible={!!benefitWindow.importedFromAirtable && !isDirty}
              />
            </div>

            <EnrolmentWindowType index={index} control={control} />

            <Section>
              <Typography variant={TypographyVariant.bodyLg2}>
                Set opening & close
              </Typography>
              <RadioButton
                key={`rule-based-${index}`}
                label="Rule based"
                checked={windowTriggerType === WindowTriggerType.RULE_BASED}
                backgroundColor={theme.palette.neutral['00']}
                onChange={() => {
                  const newTriggerTypes = [...windowTriggerTypes];
                  newTriggerTypes[index] = WindowTriggerType.RULE_BASED;
                  setWindowTriggerTypes(newTriggerTypes);
                  setDirtyValue(
                    `benefitWindows.${index}.startsOnSpecificDate`,
                    null
                  );
                  setDirtyValue(
                    `benefitWindows.${index}.endsOnSpecificDate`,
                    null
                  );
                  setDirtyValue(
                    `benefitWindows.${index}.startsOnSpecificTime`,
                    null
                  );
                  setDirtyValue(
                    `benefitWindows.${index}.endsOnSpecificTime`,
                    null
                  );
                  setDirtyValue(
                    `benefitWindows.${index}.fixedEnrolmentStartDate`,
                    null
                  );
                }}
              />
              <RowWrapper>
                <EnrolmentStartsOnDate
                  index={index}
                  control={control}
                  disabled={windowTriggerType === WindowTriggerType.DATE_RANGE}
                />
                {!isBeforeEnrollmentEnd && (
                  <>
                    <NumberInputController
                      name={`benefitWindows.${index}.endsAfter`}
                      control={control}
                      label="Ends after"
                      inputKey={String(benefitWindow.id ?? '') + 'days'}
                      hintText="Number of days the window is open after the 'Start on' date."
                      rules={{
                        required: {
                          value:
                            WindowTriggerType.RULE_BASED ===
                              windowTriggerType && !isBeforeEnrollmentEnd,
                          message: fieldCannotBeBlankErrorMessage,
                        },
                      }}
                      hasErrorCondition={
                        windowTriggerTypes[index] ===
                        WindowTriggerType.RULE_BASED
                      }
                      disabled={
                        windowTriggerType === WindowTriggerType.DATE_RANGE
                      }
                    />
                    <Controller
                      name={`benefitWindows.${index}.timeUnit`}
                      control={control}
                      rules={{
                        required: {
                          value:
                            WindowTriggerType.RULE_BASED ===
                              windowTriggerType && !isBeforeEnrollmentEnd,
                          message: fieldCannotBeBlankErrorMessage,
                        },
                      }}
                      render={({ field, fieldState }) => {
                        const { value, ref, ...rest } = field;
                        return (
                          <StyledTimeUnitSelect
                            {...rest}
                            value={value}
                            label="Time unit"
                            data-testid="time-unit-select"
                            data={TIME_UNIT_OPTIONS.map((option) => ({
                              label: option.label,
                              value: option.key,
                            }))}
                            disabled={
                              windowTriggerType === WindowTriggerType.DATE_RANGE
                            }
                            errorMessage={fieldState.error?.message}
                          />
                        );
                      }}
                    />
                  </>
                )}
                {isBeforeEnrollmentEnd &&
                  windowTriggerType !== WindowTriggerType.DATE_RANGE && (
                    <NumberInputController
                      name={`benefitWindows.${index}.startsBeforeDays`}
                      control={control}
                      label="Starts before (days)"
                      inputKey={
                        String(benefitWindow.id ?? '') + 'startsBeforeDays'
                      }
                      hintText="Opens this many days before the enrollment end date. The window closes at enrollment end."
                      rules={{
                        required: {
                          value: true,
                          message: fieldCannotBeBlankErrorMessage,
                        },
                        min: { value: 1, message: 'Must be at least 1 day' },
                      }}
                      min={1}
                      data-testid="starts-before-days-input"
                    />
                  )}
                {startsOnValues?.[index] === StartsOnEnum.AGE_THRESHOLD && (
                  <NumberInputController
                    name={`benefitWindows.${index}.ageThreshold`}
                    control={control}
                    label="Age Threshold"
                    inputKey={String(benefitWindow.id ?? '') + 'ageThreshold'}
                    hintText="Age at which the window opens."
                    rules={{
                      required: {
                        value:
                          startsOnValues?.[index] ===
                          StartsOnEnum.AGE_THRESHOLD,
                        message: fieldCannotBeBlankErrorMessage,
                      },
                    }}
                    data-testid="age-threshold-input"
                  />
                )}
              </RowWrapper>
              {windowTriggerType === WindowTriggerType.RULE_BASED &&
                !isBeforeEnrollmentEnd && (
                  <NumberInputController
                    name={`benefitWindows.${index}.startsAfterDelay`}
                    control={control}
                    label="Starts after delay (days)"
                    inputKey={
                      String(benefitWindow.id ?? '') + 'startsAfterDelay'
                    }
                    hintText="Number of days to delay the window opening after the trigger event. Leave empty for no delay."
                  />
                )}
              <DateRangeRowWrapper>
                <div>
                  <RadioButton
                    key={`date-range-${index}`}
                    label="Date range"
                    checked={windowTriggerType === WindowTriggerType.DATE_RANGE}
                    backgroundColor={theme.palette.neutral['00']}
                    onChange={() => {
                      const newTriggerTypes = [...windowTriggerTypes];
                      newTriggerTypes[index] = WindowTriggerType.DATE_RANGE;
                      setWindowTriggerTypes(newTriggerTypes);
                      setDirtyValue(`benefitWindows.${index}.endsAfter`, null);
                      setDirtyValue(`benefitWindows.${index}.timeUnit`, null);
                      setDirtyValue(
                        `benefitWindows.${index}.startsAfterDelay`,
                        null
                      );
                      setDirtyValue(
                        `benefitWindows.${index}.startsOn`,
                        StartsOnEnum.SPECIFIC_DATE
                      );
                      setDirtyValue(
                        `benefitWindows.${index}.startsOnSpecificTime`,
                        '00:00:00'
                      );
                      setDirtyValue(
                        `benefitWindows.${index}.endsOnSpecificTime`,
                        '23:59:59'
                      );
                    }}
                  />
                  <DateInput<'range'>
                    type="range"
                    label="Specify date range"
                    maxDate={maxDate}
                    minDate={minDate}
                    disabled={
                      windowTriggerType === WindowTriggerType.RULE_BASED
                    }
                    defaultValue={dateRangeValue}
                    onChange={([startDate, endDate]) => {
                      setDirtyValue(
                        `benefitWindows.${index}.startsOnSpecificDate`,
                        startDate ? format(startDate, 'yyyy-MM-dd') : undefined
                      );
                      setDirtyValue(
                        `benefitWindows.${index}.endsOnSpecificDate`,
                        endDate ? format(endDate, 'yyyy-MM-dd') : undefined
                      );
                    }}
                    errorMessage={
                      windowTriggerType === WindowTriggerType.DATE_RANGE &&
                      (!startsOnSpecificDate || !endsOnSpecificDate)
                        ? fieldCannotBeBlankErrorMessage
                        : undefined
                    }
                  />
                  <RowWrapper>
                    <Controller
                      name={`benefitWindows.${index}.startsOnSpecificTime`}
                      control={control}
                      render={({ field }) => (
                        <TimeInput
                          label="Starts on time"
                          withSeconds
                          w={160}
                          disabled={
                            windowTriggerType === WindowTriggerType.RULE_BASED
                          }
                          value={field.value ?? ''}
                          onChange={(event) => {
                            field.onChange(event.currentTarget.value || null);
                          }}
                        />
                      )}
                    />
                    <Controller
                      name={`benefitWindows.${index}.endsOnSpecificTime`}
                      control={control}
                      render={({ field }) => (
                        <TimeInput
                          label="Ends on time"
                          withSeconds
                          w={160}
                          disabled={
                            windowTriggerType === WindowTriggerType.RULE_BASED
                          }
                          value={field.value ?? ''}
                          onChange={(event) => {
                            field.onChange(event.currentTarget.value || null);
                          }}
                        />
                      )}
                    />
                  </RowWrapper>
                </div>
                <Controller
                  name={`benefitWindows.${index}.timezone`}
                  control={control}
                  render={({ field }) => {
                    const { value, ref, ...rest } = field;
                    return (
                      <StyledSelect
                        {...rest}
                        data={options}
                        label="Timezone"
                        value={value}
                        onChange={(value) => {
                          const parsedTimezone = parseTimezone(
                            value as ITimezone
                          );
                          if (parsedTimezone) {
                            setDirtyValue(
                              `benefitWindows.${index}.timezone`,
                              parsedTimezone.value
                            );
                          }
                        }}
                        searchable
                      />
                    );
                  }}
                />
              </DateRangeRowWrapper>
              {windowTypeValues[index] === WindowTypeEnum.OPT_IN_WINDOW &&
                windowTriggerType === WindowTriggerType.DATE_RANGE && (
                  <DateInput<'default'>
                    label="Fixed enrolment start date"
                    hintText="Pins the coverage start date for employees who opt in during this window. Leave empty to use the default delay logic."
                    value={
                      fixedEnrolmentStartDateValues[index]
                        ? parseDateValue(
                            fixedEnrolmentStartDateValues[index] ?? ''
                          )
                        : null
                    }
                    onChange={(date) => {
                      setDirtyValue(
                        `benefitWindows.${index}.fixedEnrolmentStartDate`,
                        date ? format(date, 'yyyy-MM-dd') : null
                      );
                    }}
                  />
                )}
            </Section>

            {getWindowType(index) === WindowTypeEnum.CONFIG_WINDOW && (
              <LifeEvents control={control} />
            )}
          </ColumnWrapper>
        );
      })}

      {errors?.nonFieldErrors && (
        <NonFieldErrorMessage>{errors?.nonFieldErrors[0]}</NonFieldErrorMessage>
      )}
    </Wrapper>
  );
};

export default memo(EnrolmentWindows);

const ColumnWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    background-color: ${theme.palette.primary.bg};
    padding: ${theme.spacing(3)};
    gap: ${theme.spacing(3)};
  `}
`;

const Section = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const RowWrapper = styled.div`
  ${({ theme, center }: { theme: ITheme; center?: boolean }) => css`
    display: flex;
    gap: ${theme.spacing(2.5)};
    align-items: ${center ? 'center' : 'flex-start'};
  `}
`;

const AddAndNoSubPriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(3)};
    border-radius: 10px;
    border: 1px solid ${theme.palette.neutral[60]};
    gap: ${theme.spacing(2)};
  `}
`;

const StyledInlineTextButton = styled(InlineTextButton)`
  ${({ theme }) => theme.typography.bodyLg2};
`;

const NonFieldErrorMessage = styled.div`
  color: rgb(228, 50, 27);
`;

const DateRangeRowWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2.5)};
    align-items: flex-end;
    width: 100%;
  `}
`;

const StyledSelect = styled(Select)`
  width: 335px;
`;

const StyledTimeUnitSelect = styled(Select)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(-1)};
  `}
`;
